/**
 * Phase 5E — Admin V5 Bookings & Series panel (backbone).
 *
 * Surfaces:
 *  - Multi-tutor assignment editor per student (Manage Tutors)
 *  - Create Booking Series wizard (weekly recurring or one-off)
 *  - Series list with payment link + copy/email/cancel actions
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, RefreshCcw, Users, UserPlus, Copy, Mail, X, Calendar,
  FileDown, Link as LinkIcon, Plus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const AdminBookingSeriesPanel = () => {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null); // student email
  const [studentFilter, setStudentFilter] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get('/api/admin/booking-series');
      setSeries(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load series');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendLink = async (sid, to) => {
    try {
      const r = await ax.post(`/api/admin/booking-series/${sid}/send-payment-link`, { to });
      if (r.data?.result?.status === 'success') {
        toast.success(`Payment link emailed to ${r.data.to}`);
      } else {
        toast.warning(`Email status: ${r.data?.result?.status || 'unknown'}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send link');
    }
  };

  const copyLink = (url) => {
    if (!url) { toast.error('No payment link on this series'); return; }
    navigator.clipboard?.writeText(url).then(
      () => toast.success('Payment link copied'),
      () => toast.error('Could not copy — long-press to copy manually'),
    );
  };

  const cancel = async (sid) => {
    if (!window.confirm('Cancel this series? Unpaid bookings will be cancelled. Paid sessions are preserved.')) return;
    try {
      await ax.post(`/api/admin/booking-series/${sid}/cancel`, {});
      toast.success('Series cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const deleteSeriesFull = async (sid) => {
    if (!window.confirm('Permanently delete this Booking Series? Pending bookings will be removed. Paid records and completed bookings are preserved.')) return;
    try {
      const r = await ax.delete(`/api/admin/booking-series/${sid}/full`);
      if (r.data?.deleted) toast.success(`Series deleted (${r.data?.bookings_deleted || 0} bookings)`);
      else toast.warning(r.data?.reason || 'Could not delete');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const downloadStudentPdf = async (email) => {
    try {
      const r = await ax.get(`/api/admin/students/${encodeURIComponent(email)}/timetable.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = `timetable-${email}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF download failed');
    }
  };

  const visible = series.filter((s) => !studentFilter ||
    (s.student_email || '').toLowerCase().includes(studentFilter.toLowerCase()));

  return (
    <Card data-testid="admin-booking-series-panel">
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
        <div>
          <CardTitle className="flex items-center gap-2"><Calendar className="h-5 w-5" /> Bookings & Series</CardTitle>
          <p className="text-sm text-gray-500 mt-1">Multi-tutor assignments · weekly timetables · payment links (AUD source, USD checkout).</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="series-refresh">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw className="h-4 w-4" />} Refresh
          </Button>
          <Button size="sm" className="bg-red-700 hover:bg-red-800 text-white gap-1" onClick={() => setCreateOpen(true)} data-testid="create-series-btn">
            <Plus className="h-4 w-4" /> Create Series
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Input placeholder="Filter by student email…" value={studentFilter}
            onChange={(e) => setStudentFilter(e.target.value)} className="max-w-sm h-9 text-sm" data-testid="series-filter" />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : visible.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-400 italic" data-testid="series-empty">
            No booking series yet. Create one to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((s) => (
              <div key={s.series_id} className="border rounded-xl p-4" data-testid={`series-row-${s.series_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-gray-900">{s.subject} · {s.student_name || s.student_email}</div>
                    <div className="text-[11px] text-gray-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span>{s.total_sessions} × {s.duration_minutes}min</span>
                      <span>·</span>
                      <span>{s.day_of_week} {s.start_time}</span>
                      <span>·</span>
                      <span>starts {s.start_date}</span>
                      <span>·</span>
                      <span>Tutor: {s.tutor_name || s.tutor_email}</span>
                      {s.parent_email && (<><span>·</span><span>Parent: {s.parent_email}</span></>)}
                    </div>
                    <div className="text-xs mt-1.5">
                      <strong className="text-gray-900">AUD {(s.total_amount_aud_cents / 100).toFixed(2)}</strong>
                      <span className="text-gray-500"> · {s.package_type === 'intensive_package' ? 'Intensive' : 'Single sessions'}</span>
                      <Badge className={`ml-2 text-[10px] ${s.payment_link_status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-200 text-gray-700'}`}>
                        {s.payment_link_status || 'pending'}
                      </Badge>
                      <Badge className="ml-1 text-[10px] bg-blue-100 text-blue-800">{s.payment_status || 'pending'}</Badge>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => copyLink(s.payment_link)}
                      className="text-[10px] gap-1" data-testid={`series-copy-${s.series_id}`} disabled={!s.payment_link}>
                      <Copy className="h-3 w-3" /> Copy link
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => sendLink(s.series_id, 'student')}
                      className="text-[10px] gap-1" data-testid={`series-email-student-${s.series_id}`} disabled={!s.payment_link}>
                      <Mail className="h-3 w-3" /> Email student
                    </Button>
                    {s.parent_email && (
                      <Button size="sm" variant="outline" onClick={() => sendLink(s.series_id, 'parent')}
                        className="text-[10px] gap-1" data-testid={`series-email-parent-${s.series_id}`} disabled={!s.payment_link}>
                        <Mail className="h-3 w-3" /> Email parent
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => setAssignOpen(s.student_email)}
                      className="text-[10px] gap-1" data-testid={`series-tutors-${s.series_id}`}>
                      <Users className="h-3 w-3" /> Tutors
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadStudentPdf(s.student_email)}
                      className="text-[10px] gap-1" data-testid={`series-pdf-${s.series_id}`}>
                      <FileDown className="h-3 w-3" /> PDF
                    </Button>
                    {s.payment_link_status !== 'cancelled' && (
                      <Button size="sm" variant="outline" onClick={() => cancel(s.series_id)}
                        className="text-[10px] gap-1 text-red-700 border-red-200 hover:bg-red-50"
                        data-testid={`series-cancel-${s.series_id}`}>
                        <X className="h-3 w-3" /> Cancel
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => deleteSeriesFull(s.series_id)}
                      className="text-[10px] gap-1 text-rose-800 bg-rose-50 border-rose-200 hover:bg-rose-100"
                      data-testid={`series-delete-${s.series_id}`}>
                      <X className="h-3 w-3" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {createOpen && <CreateSeriesModal onClose={() => setCreateOpen(false)} onCreated={() => { setCreateOpen(false); fetchAll(); }} />}
      {assignOpen && <ManageTutorsModal studentEmail={assignOpen} onClose={() => setAssignOpen(null)} />}
    </Card>
  );
};

// ---------------------- Create Series Modal ----------------------
const CreateSeriesModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    student_email: '',
    parent_email: '',
    tutor_email: '',
    subject: '',
    program: '',
    start_date: new Date().toISOString().slice(0, 10),
    start_time: '17:00',
    num_sessions: 8,
    repeat_weekly: true,
    duration_minutes: 60,
    package_type: 'single_session',
    timezone: 'Australia/Melbourne',
  });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.student_email || !form.tutor_email || !form.subject || !form.start_date || !form.start_time) {
      toast.error('Please fill in student, tutor, subject, start date and time.');
      return;
    }
    setBusy(true);
    try {
      const r = await ax.post('/api/admin/booking-series', {
        ...form,
        parent_email: form.parent_email || null,
        program: form.program || null,
        num_sessions: parseInt(form.num_sessions, 10),
      });
      toast.success(`Series created · ${r.data?.bookings_created} bookings · payment link ${r.data?.series?.payment_link ? 'ready' : 'pending'}`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Series creation failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="create-series-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
        <h3 className="text-lg font-bold text-gray-900 mt-1">Create booking series</h3>
        <p className="text-xs text-gray-500 mt-1">Generate N weekly bookings + a Paystack payment link.</p>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Field label="Student email *"><Input value={form.student_email} onChange={(e) => set('student_email', e.target.value)} data-testid="series-form-student" /></Field>
          <Field label="Parent email (optional)"><Input value={form.parent_email} onChange={(e) => set('parent_email', e.target.value)} data-testid="series-form-parent" /></Field>
          <Field label="Tutor email *"><Input value={form.tutor_email} onChange={(e) => set('tutor_email', e.target.value)} data-testid="series-form-tutor" /></Field>
          <Field label="Subject *"><Input value={form.subject} onChange={(e) => set('subject', e.target.value)} placeholder="e.g. Mathematics" data-testid="series-form-subject" /></Field>
          <Field label="Program"><Input value={form.program} onChange={(e) => set('program', e.target.value)} placeholder="e.g. Selective Entry" data-testid="series-form-program" /></Field>
          <Field label="Start date *"><Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} data-testid="series-form-date" /></Field>
          <Field label="Start time *"><Input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} data-testid="series-form-time" /></Field>
          <Field label="Duration (min)"><Input type="number" value={form.duration_minutes} onChange={(e) => set('duration_minutes', e.target.value)} data-testid="series-form-duration" /></Field>
          <Field label="Sessions *"><Input type="number" value={form.num_sessions} onChange={(e) => set('num_sessions', e.target.value)} data-testid="series-form-sessions" disabled={form.package_type === 'intensive_package'} /></Field>
          <Field label="Package type">
            <select value={form.package_type} onChange={(e) => set('package_type', e.target.value)}
              className="w-full h-9 px-2 rounded border border-gray-300 text-sm" data-testid="series-form-package">
              <option value="single_session">Single sessions (per-week)</option>
              <option value="intensive_package">Intensive (4 sessions)</option>
            </select>
          </Field>
          <Field label="Timezone"><Input value={form.timezone} onChange={(e) => set('timezone', e.target.value)} data-testid="series-form-tz" /></Field>
          <Field label="Repeat weekly">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.repeat_weekly} onChange={(e) => set('repeat_weekly', e.target.checked)} data-testid="series-form-repeat" />
              Repeat weekly
            </label>
          </Field>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="series-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-red-700 hover:bg-red-800 text-white" data-testid="series-form-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <LinkIcon className="h-4 w-4 mr-1" />}
            Create & generate payment link
          </Button>
        </div>
      </div>
    </div>
  );
};

// ---------------------- Manage Tutors Modal ----------------------
const ManageTutorsModal = ({ studentEmail, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState({ tutor_email: '', subject_area: '', program: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ax.get(`/api/admin/students/${encodeURIComponent(studentEmail)}/tutors`);
      setRows(r.data?.items || []);
    } catch (_e) { /* */ } finally { setLoading(false); }
  }, [studentEmail]);

  useEffect(() => { load(); }, [load]);

  const add = async (confirmDup = false) => {
    if (!adding.tutor_email || !adding.subject_area) { toast.error('tutor email + subject required'); return; }
    try {
      await ax.post(`/api/admin/students/${encodeURIComponent(studentEmail)}/tutors`, {
        ...adding, confirm_duplicate: confirmDup,
      });
      toast.success('Tutor assigned');
      setAdding({ tutor_email: '', subject_area: '', program: '' });
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.code === 'DUPLICATE_ASSIGNMENT') {
        if (window.confirm(det.message)) return add(true);
        return;
      }
      toast.error(det?.message || det || 'Add failed');
    }
  };

  const removeRow = async (id) => {
    if (!window.confirm('Remove this tutor assignment?')) return;
    try {
      await ax.delete(`/api/admin/students/${encodeURIComponent(studentEmail)}/tutors/${id}`);
      toast.success('Removed');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async (row) => {
    try {
      await ax.patch(`/api/admin/students/${encodeURIComponent(studentEmail)}/tutors/${row.id}`,
        { active: !row.active });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Patch failed'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="manage-tutors-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-[10px] uppercase tracking-[3px] text-red-700 font-bold">WINHOUSE TUTORS</div>
        <h3 className="text-lg font-bold text-gray-900 mt-1">Tutors for {studentEmail}</h3>

        <div className="mt-4 space-y-2">
          {loading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : rows.length === 0 ? (
            <div className="text-sm italic text-gray-400">No tutors assigned yet.</div>
          ) : (
            rows.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 border border-gray-200 rounded-lg p-2.5" data-testid={`tutor-assignment-${r.id}`}>
                <div>
                  <div className="text-sm font-bold text-gray-900">{r.tutor_name || r.tutor_email} · {r.subject_area}</div>
                  <div className="text-[10px] text-gray-500">{r.tutor_email} · {r.program || '—'} · source: {r.source}</div>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => toggleActive(r)} className="text-[10px]">
                    {r.active ? 'Set inactive' : 'Activate'}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => removeRow(r.id)}
                    className="text-[10px] text-red-700 border-red-200">Remove</Button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="mt-5 border-t pt-4">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Add another tutor</div>
          <div className="grid sm:grid-cols-3 gap-2">
            <Input value={adding.tutor_email} onChange={(e) => setAdding({ ...adding, tutor_email: e.target.value })}
              placeholder="tutor email" data-testid="assign-tutor-email" />
            <Input value={adding.subject_area} onChange={(e) => setAdding({ ...adding, subject_area: e.target.value })}
              placeholder="subject" data-testid="assign-tutor-subject" />
            <Input value={adding.program} onChange={(e) => setAdding({ ...adding, program: e.target.value })}
              placeholder="program (optional)" data-testid="assign-tutor-program" />
          </div>
          <div className="mt-2 flex justify-end">
            <Button onClick={() => add(false)} className="bg-red-700 hover:bg-red-800 text-white gap-1" data-testid="assign-tutor-submit">
              <UserPlus className="h-4 w-4" /> Assign
            </Button>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="manage-tutors-close">Close</Button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block text-sm">
    <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold block mb-1">{label}</span>
    {children}
  </label>
);

export default AdminBookingSeriesPanel;
